<template>
  <div>
    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="handleRefetchData"
    />

    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>

      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Asignación efectiva actual -->
    <b-card class="mb-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-50">{{ $t("assigned_regions.current_assignment") }}</h5>

          <div class="mb-50">
            <b-badge
              v-for="sector in currentSectors"
              :key="sector.id"
              pill
              variant="light-primary"
              class="mr-50 mb-50"
            >
              {{ sector.name || sector.description || `#${sector.id}` }}
            </b-badge>
          </div>

          <div class="text-muted mb-25">
            {{ $t("assigned_regions.analyst") }}:
            <strong>{{ currentAnalystLabel }}</strong>
          </div>

          <div class="text-muted mb-25">
            {{ $t("assigned_regions.supervisor") }}:
            <strong>{{ currentSupervisorLabel }}</strong>
          </div>

          <div class="text-muted">
            {{ $t("assigned_regions.scheduled") }}:
            <strong>{{ currentScheduledLabel }}</strong>
          </div>
        </div>

        <b-button
          variant="primary"
          v-if="
            $can('create', 'UserAssignedRegion') || $can('update', 'UserAssignedRegion')
          "
          @click="openAdd"
        >
          {{ $t("assigned_regions.schedule_regions") }}
        </b-button>
      </div>
    </b-card>

    <!-- Filters -->
    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
      v-if="showFilters"
    >
      <template v-slot:main-filters>
        <filters
          :user-options="userOptions"
          :sector-options="sectorOptions"
          :supervisor-filter.sync="supervisorFilter"
          :sector-filter.sync="sectorFilter"
          :scheduled-filter.sync="scheduledFilter"
        />
      </template>
    </filter-modal>

    <!-- Table -->
    <b-card-group deck :class="{ 'mx-2': showFilters, 'm-0': !showFilters }">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('table.search') + '...'"
                />
              </div>
            </b-col>
          </b-row>
        </div>

        <b-table
          ref="refListTable"
          class="position-relative"
          :items="fetchList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          :empty-text="$t('no_record_found')"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #head()="data">
            <span>{{ $tc(data.label, 1) }}</span>
          </template>

          <template #cell(sectors)="data">
            <b-badge
              v-for="sector in data.item.sectors || []"
              :key="sector.id"
              pill
              variant="light-primary"
              class="mr-50 mb-50"
            >
              {{ sector.name || sector.description || `#${sector.id}` }}
            </b-badge>
          </template>

          <template #cell(analyst)="data">
            <span>{{ formatUser(data.item.analyst) }}</span>
          </template>

          <template #cell(supervisor)="data">
            <span>{{ formatUser(data.item.supervisor) }}</span>
          </template>

          <template #cell(scheduled)="data">
            <span>{{ formatScheduled(data.item.scheduled) }}</span>
          </template>

          <template #cell(init_date)="data">
            <span>{{ formatDateAssigned(data.item.init_date) }}</span>
          </template>

          <template #cell(end_date)="data">
            <span>{{ formatDateAssigned(data.item.end_date) }}</span>
          </template>

          <template #cell(actions)="data">
            <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                @click="openEdit(data.item)"
                v-if="
                  $can('update', 'UserAssignedRegion') &&
                  Number(data.item.scheduled) === 1
                "
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">{{
                  $t("actions.edit")
                }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                @click="confirmDelete(data.item.id)"
                v-if="$can('delete', 'UserAssignedRegion')"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">{{
                  $t("actions.delete")
                }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted text-capitalize">
                {{
                  $t("table.showing", {
                    from: dataMeta.from,
                    to: dataMeta.to,
                    of: dataMeta.of,
                  })
                }}
              </span>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <Pagination
                :totalList="totalList"
                :currentPage="currentPage"
                @updatePagination="update"
                :perPage="perPage"
              />
            </b-col>
          </b-row>
        </div>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BCardGroup,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BBadge,
} from "bootstrap-vue";
import { ref, computed, onMounted } from "@vue/composition-api";
import vSelect from "vue-select";
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { decryptData } from "@/utils/decryptUserDataUtils";

import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import Pagination from "@/components/Pagination.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

export default {
  components: {
    Sidenav,
    Filters,
    BCard,
    BCardGroup,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BButton,
    BDropdown,
    BDropdownItem,
    BBadge,
    vSelect,
    BreadcrumbMenu,
    Pagination,
    FilterModal,
    ButtonFilters,
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const toast = useToast();

    const user = JSON.parse(decryptData(window.localStorage.userData));
    const analystUserId = user.id;

    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      formatUser,
      formatScheduled,
      userOptions,
      sectorOptions,
      supervisorFilter,
      sectorFilter,
      scheduledFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
    } = useList(props.showFilters);

    const loadCurrentAssignment = () =>
      store.dispatch("user_assigned_region/getCurrent", {
        analyst_user_id: analystUserId,
      });

    const currentAssignment = computed(
      () => store.getters["user_assigned_region/getCurrent"]
    );

    const currentSectors = computed(() => currentAssignment.value?.sectors || []);

    const currentAnalystLabel = computed(() => {
      const analyst = currentAssignment.value?.analyst;
      return analyst
        ? analyst.name || analyst.full_name || analyst.email || `#${analyst.id}`
        : "-";
    });

    const currentSupervisorLabel = computed(() => {
      const sup = currentAssignment.value?.supervisor;
      return sup ? sup.name || sup.full_name || sup.email || `#${sup.id}` : "-";
    });

    const currentScheduledLabel = computed(() => {
      if (!currentAssignment.value) return "-";
      return formatScheduled(currentAssignment.value.scheduled);
    });

    onMounted(() => {
      loadCurrentAssignment();
    });

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
    };

    const openAdd = () => {
      isAdd.value = true;
      itemEdit.value = {};
      isSidebarActive.value = true;
    };

    const update = (page) => {
      currentPage.value = page;
    };

    const handleRefetchData = () => {
      refetchData();
      loadCurrentAssignment();
    };

    const confirmDelete = function (id) {
      const vm = context.parent;

      vm.$swal({
        title: vm.$i18n.t("messages.delete.title"),
        text: vm.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: vm.$i18n.t("actions.delete"),
        cancelButtonText: vm.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (!result.value) return;

        store
          .dispatch("user_assigned_region/delete", id)
          .then((response) => {
            toast({
              component: ToastificationContent,
              props: {
                title: response?.message || "Registro eliminado",
                icon: "CheckIcon",
                variant: "success",
              },
            });

            handleRefetchData();
          })
          .catch((err) => {
            const msg = err?.response?.data?.message || "Error eliminando el registro";
            toast({
              component: ToastificationContent,
              props: {
                title: msg,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          });
      });
    };

    return {
      isSidebarActive,
      itemEdit,
      isAdd,

      openEdit,
      openAdd,
      confirmDelete,
      update,
      handleRefetchData,

      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      formatUser,
      formatScheduled,

      userOptions,
      sectorOptions,

      supervisorFilter,
      sectorFilter,
      scheduledFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,

      currentAssignment,
      currentSectors,
      currentAnalystLabel,
      currentSupervisorLabel,
      currentScheduledLabel,
    };
  },
};
</script>
